import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { toInteger } from 'lodash'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PaginationWithPageSelector } from '../../../Component/EnhancedData/Pagination/PaginationWithPageSelector'
import { SortableTable } from '../../../Component/EnhancedData/SortableTable'
import { tableColumnTypes } from '../../../Component/EnhancedData/SortableTable/TableBody'
import { RowDataWithKey, TableColumn } from '../../../Component/EnhancedData/SortableTable/Types'
import { mapToTransactionRowData } from '../../../Helper/ViewEDTransactionsHelper'
import { EDTransactionRowData } from '../../../Type/EnhancedData'
import Event from '../../../Event'
import * as EDT from '../../../Type/EnhancedData'
import {
  toEnhancedDataTransactionsRequest,
} from '../../../Helper/EnhancedData/ManageEnhancedDataMapper'
import { raiseNetworkError } from '../../../Helper/util'
import { NETWORK_ERROR_BANNER_MESSAGE } from '../../../Helper/EnhancedData/Constants'

const tableColumnsRequired: TableColumn<EDTransactionRowData>[] = [
  {
    id: 'transaction_id',
    displayName: 'Transaction ID',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'amount',
    displayName: 'Amount',
    dataType: tableColumnTypes.money,
    isSortable: true,
  },
  {
    id: 'card_masked',
    displayName: 'Card #',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'name',
    displayName: 'Name',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'card_brand',
    displayName: 'Card Type',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'navigation_href',
    displayName: '',
    dataType: tableColumnTypes.string,
    isSortable: false,
  },
]

const tableColumnsOptional: TableColumn<EDTransactionRowData>[] = [
  {
    id: 'transaction_id',
    displayName: 'Transaction ID',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'amount',
    displayName: 'Amount',
    dataType: tableColumnTypes.money,
    isSortable: true,
  },
  {
    id: 'card_masked',
    displayName: 'Card #',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'name',
    displayName: 'Name',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'card_brand',
    displayName: 'Card Type',
    dataType: tableColumnTypes.string,
    isSortable: true,
  },
  {
    id: 'card_level',
    displayName: 'Level',
    dataType: tableColumnTypes.number,
    isSortable: true,
  },
  {
    id: 'navigation_href',
    displayName: '',
    dataType: tableColumnTypes.string,
    isSortable: false,
  },
]

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
const DEFAULT_NUMBER_OF_RECORDS = 0
const DEFAULT_OFFSET = 0
const DEFAULT_SHOW_TAB_CONTENT = false

interface ViewTransactionsProps {
  symbol: Symbol
  isEnhancedDataRequired: boolean
}

export function ViewTransactions ({ symbol, isEnhancedDataRequired }: ViewTransactionsProps): React.JSX.Element {
  const [showTabContent, setShowTabContent] = useState<boolean>(DEFAULT_SHOW_TAB_CONTENT)
  const [tableColumns, setTableColumns] = useState<TableColumn<EDTransactionRowData>[]>(tableColumnsRequired)
  const navigate = useNavigate()
  const [rows, setRows] = useState<RowDataWithKey<EDTransactionRowData>[]>([])
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE) // Default selected page
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE) // Default page size
  const [numberOfRecords, setNumberOfRecords] = useState<number>(DEFAULT_NUMBER_OF_RECORDS) // Default number of records
  const [offset, setOffset] = useState<number>(DEFAULT_OFFSET) // Default offset

  const appContext = useContext<AppCtxI>(AppCtx)
  const merchantId: number = toInteger(appContext.merchant)

  const navigationLinkClickHandler = useCallback((e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    const href: string | null = e.currentTarget.getAttribute('href')
    const { cardLevel } = e.currentTarget.dataset
    if (href) navigate(href, { state: { cardLevel } })
  }, [navigate])

  const sortClickHandler = (sortedRows: RowDataWithKey<EDTransactionRowData>[]): void => {
    setRows(sortedRows)
  }

  // Handle Pagination Interactions
  const handlePageChange = (targetPage: number): void => {
    setCurrentPage(targetPage)
    const newOffset = (targetPage - 1) * pageSize
    setOffset(newOffset)
    console.debug(`Fetch page index: ${targetPage} using size payload: ${pageSize}`)
  }

  const handlePageSizeChange = (targetSize: number): void => {
    setPageSize(targetSize)
    setCurrentPage(DEFAULT_PAGE) // Always return to the default page after changing page size
    setOffset(DEFAULT_OFFSET)
    console.debug(`Resetting offset to 0. New viewport payload limit size: ${targetSize}`)
  }

  // API interactions
  const fetchRequiredTransactions = useCallback(() => {
    const requestPayload: EDT.EnhancedDataTransactionsRequest = toEnhancedDataTransactionsRequest(
      merchantId,
      offset,
      pageSize,
      isEnhancedDataRequired
    )
    PubSub.pub(Event.GET_ENHANCED_DATA_TRANSACTIONS_REQUEST, requestPayload)
    setShowTabContent(false)
  }, [merchantId, offset, pageSize, isEnhancedDataRequired])

  const handleErrorResponse = useCallback((rawError: any): void => {
    setShowTabContent(false)
    raiseNetworkError(NETWORK_ERROR_BANNER_MESSAGE)
  }, [])

  useEffect(() => {
    fetchRequiredTransactions()
  }, [fetchRequiredTransactions])

  useEffect(() => {
    if (isEnhancedDataRequired) {
      setTableColumns(tableColumnsRequired)
    } else {
      setTableColumns(tableColumnsOptional)
    }
  }, [isEnhancedDataRequired])

  useEffect(() => {
    PubSub.sub(symbol, Event.GET_ENHANCED_DATA_TRANSACTIONS_RESPONSE, (data) => {
      const transactionList: EDT.TransactionSearchRecordForManualFill[] = data.data.items
      const recordCount: number = data.data.total_count
      const transactionRows: RowDataWithKey<EDTransactionRowData>[] = transactionList.map(
        transaction => mapToTransactionRowData(transaction, navigationLinkClickHandler),
      )

      setShowTabContent(true)
      setNumberOfRecords(recordCount)
      setRows(transactionRows)
    })
    PubSub.sub(symbol, Event.GET_ENHANCED_DATA_TRANSACTIONS_RESPONSE_ERROR, (rawError: any) => {
      handleErrorResponse(rawError)
    })
    return () => { PubSub.unsub(symbol) }
  }, [merchantId, offset, pageSize, handleErrorResponse, navigationLinkClickHandler, symbol, isEnhancedDataRequired])

  return (
    <div className="view-transactions">
      { showTabContent &&
      (
        <>
        {isEnhancedDataRequired && (
        <div className="tab-title">
          These L3 transactions <strong> require </strong> Enhanced Data in order to qualify for better processing rates.
        </div>
        )}
        {!isEnhancedDataRequired && (
        <div className="tab-title">
          These transactions may include Enhanced Data, but they will not qualify for better processing rates.
        </div>
        )}
        <div className='horizontal-break'/>
        <div className="sortable-table">
          {/* List Transaction Table Here */}
          {/* TODO: Use a common PaginatedTable component to populate it */}
          {/* Create the structure here and indentify the variables */}
          {/* Refactor the code and move it to a new component for reusability */}

          <SortableTable
            columns={tableColumns}
            rows={rows}
            onSortClick={sortClickHandler}
          />
          {numberOfRecords > 0 &&
            <PaginationWithPageSelector
              page={currentPage}
              pageSize={pageSize}
              numberOfRecords={numberOfRecords}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          }
        </div>
        </>
      )
      }
    </div>
  )
}
